import QRCode from "qrcode";
import { seedBusinesses, seedRequests } from "./data";
import type { BusinessProfile, PaymentRequest, ReportRecord } from "./types";

const BUSINESSES_KEY = "lucaden.businesses";
const REQUESTS_KEY = "lucaden.paymentRequests";
const REPORTS_KEY = "lucaden.reports";

export function siteOrigin() {
  if (typeof window === "undefined") return "https://lucaden.com";
  const localHosts = new Set(["localhost", "127.0.0.1", "::1"]);
  return localHosts.has(window.location.hostname) ? "https://lucaden.com" : window.location.origin;
}

export function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function money(amount?: string | number, currency = "HKD") {
  const numeric = Number(amount || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  return new Intl.NumberFormat("en-HK", {
    style: "currency",
    currency,
    maximumFractionDigits: numeric % 1 === 0 ? 0 : 2
  }).format(numeric);
}

export function maskFps(value: string) {
  if (!value) return "Not provided";
  const normalized = value.replace(/\s/g, "");
  if (normalized.includes("@")) {
    const [name, domain] = normalized.split("@");
    return `${name.slice(0, 2)}***@${domain}`;
  }
  if (normalized.length <= 4) return "****";
  return `${normalized.slice(0, 3)}${"*".repeat(Math.min(5, normalized.length - 5))}${normalized.slice(-2)}`;
}

export function verificationLabel(level: number) {
  if (level >= 5) return "Enhanced verified";
  if (level >= 4) return "Business verified";
  if (level >= 3) return "Document submitted";
  if (level >= 2) return "Phone verified";
  if (level >= 1) return "Email verified";
  return "Not verified";
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(key);
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T) {
  window.localStorage.setItem(key, JSON.stringify(value));
}

export function getBusinesses() {
  return readJson<BusinessProfile[]>(BUSINESSES_KEY, seedBusinesses);
}

export function saveBusinesses(businesses: BusinessProfile[]) {
  writeJson(BUSINESSES_KEY, businesses);
}

export function getRequests() {
  return readJson<PaymentRequest[]>(REQUESTS_KEY, seedRequests);
}

export function saveRequests(requests: PaymentRequest[]) {
  writeJson(REQUESTS_KEY, requests);
}

export function getReports() {
  return readJson<ReportRecord[]>(REPORTS_KEY, []);
}

export function saveReports(reports: ReportRecord[]) {
  writeJson(REPORTS_KEY, reports);
}

export async function toQrDataUrl(value: string, width = 520) {
  return QRCode.toDataURL(value, {
    width,
    margin: 2,
    errorCorrectionLevel: "M",
    color: { dark: "#0f172a", light: "#ffffff" }
  });
}

export async function downloadQrPng(url: string, filename: string) {
  const dataUrl = await toQrDataUrl(url, 1024);
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  link.click();
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

export async function downloadBrandedQrPng(url: string, filename: string, logoDataUrl = "", label = "Scan to pay") {
  const qrDataUrl = await toQrDataUrl(url, 920);
  const canvas = document.createElement("canvas");
  canvas.width = 1200;
  canvas.height = 1380;
  const ctx = canvas.getContext("2d");
  if (!ctx) return downloadQrPng(url, filename);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#0f766e";
  ctx.fillRect(0, 0, canvas.width, 132);
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 48px Inter, Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("Lucaden FPS payment QR", canvas.width / 2, 82);
  const qrImage = await loadImage(qrDataUrl);
  ctx.drawImage(qrImage, 140, 210, 920, 920);
  if (logoDataUrl) {
    const logo = await loadImage(logoDataUrl);
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.roundRect(500, 570, 200, 200, 28);
    ctx.fill();
    ctx.drawImage(logo, 525, 595, 150, 150);
  }
  ctx.fillStyle = "#17212f";
  ctx.font = "bold 42px Inter, Arial, sans-serif";
  ctx.fillText(label.slice(0, 54), canvas.width / 2, 1215);
  ctx.fillStyle = "#64717d";
  ctx.font = "28px Inter, Arial, sans-serif";
  ctx.fillText("FPS payments happen in the payer's own bank or wallet app.", canvas.width / 2, 1270);
  const link = document.createElement("a");
  link.href = canvas.toDataURL("image/png");
  link.download = filename;
  link.click();
}

export function downloadCsv(filename: string, rows: Record<string, string>[]) {
  const headers = Object.keys(rows[0] || { empty: "" });
  const csv = [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const cell = row[header] ?? "";
          return `"${String(cell).replace(/"/g, '""')}"`;
        })
        .join(",")
    )
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function copyText(value: string) {
  return navigator.clipboard.writeText(value);
}

export async function apiJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...(options.headers || {})
    }
  });
  const data = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(data.error || `Request failed: ${response.status}`);
  return data as T;
}

export function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function getRoute() {
  const path = window.location.pathname;
  const search = new URLSearchParams(window.location.search);
  return { path, search };
}
